import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Hammer, Menu, X, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_HOME } from "@/lib/api";
import { useT } from "@/i18n";

const NAV = {
  guest: [{ to: "/casa-e-villa", key: "nav.casaVilla" }, { to: "/cura-degli-interni", key: "nav.interni" }, { to: "/365", key: "nav.p365" }, { to: "/#contatti", key: "nav.contatti" }],
  customer: [{ to: "/app/casa", key: "nav.myHome" }, { to: "/app", key: "nav.myRequests" }, { to: "/casa-e-villa", key: "nav.casaVilla" }, { to: "/cura-degli-interni", key: "nav.interni" }, { to: "/365", key: "nav.p365" }],
  admin: [{ to: "/admin", key: "nav.requests" }, { to: "/admin/case", key: "nav.homes" }, { to: "/admin/calendario", key: "nav.calendar" }, { to: "/admin/team", key: "nav.team" }, { to: "/admin/vetrina", key: "nav.showcase" }],
};

export function LangSwitch({ dark = false }) {
  const { locale, setLocale } = useT();
  const next = locale === "it" ? "en" : "it";
  return (
    <button data-testid="lang-switch" onClick={() => setLocale(next)} title={next.toUpperCase()}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-mono font-semibold uppercase transition-colors ${dark ? "border-white/20 text-white/80 hover:bg-white/10" : "border-craft text-ink/70 hover:bg-ink/5"}`}>
      <Globe size={12} /> {locale}
    </button>
  );
}

export function Brand({ dark = false }) {
  return (
    <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5">
      <span className={`grid h-9 w-9 place-items-center rounded-xl ${dark ? "bg-safety text-white" : "bg-ink text-white"}`}>
        <Hammer size={18} />
      </span>
      <span className={`font-serif text-xl font-bold tracking-tight ${dark ? "text-white" : "text-ink"}`}>Ci penso <span className="text-terracotta">io</span></span>
    </Link>
  );
}

export function Shell({ children, wide = false }) {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const links = user ? NAV[user.role] || [] : NAV.guest;
  const testId = (l) => `nav-${l.key.split(".")[1].toLowerCase()}`;
  const renderLink = (l, cls) => l.to.includes("#")
    ? <a key={l.to} href={l.to} className={cls} data-testid={testId(l)} onClick={() => setOpen(false)}>{t(l.key)}</a>
    : <NavLink key={l.to} to={l.to} end className={({ isActive }) => `${cls} ${isActive ? "bg-ink/5 text-ink" : ""}`} data-testid={testId(l)} onClick={() => setOpen(false)}>{t(l.key)}</NavLink>;
  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 border-b border-craft/80 bg-linen/80 backdrop-blur-md">
        <div className={`mx-auto flex h-16 items-center justify-between px-5 ${wide ? "max-w-[1400px]" : "max-w-6xl"}`}>
          <Brand />
          <nav className="hidden lg:flex items-center gap-1">{links.map((l) => renderLink(l, "btn-ghost"))}</nav>
          <div className="flex items-center gap-2">
            <LangSwitch />
            {user ? (
              <>
                <div className="hidden sm:block text-right leading-tight">
                  <div className="text-sm font-semibold" data-testid="header-user-name">{user.name}</div>
                  <div className="text-[11px] uppercase tracking-wider text-ink/50">{t(`role.${user.role}`)}</div>
                </div>
                <button data-testid="logout-button" onClick={async () => { await logout(); nav("/"); }} className="btn-ghost" title={t("nav.logout")}><LogOut size={16} /></button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link" className="btn-ghost">{t("nav.login")}</Link>
                <Link to="/register" data-testid="nav-register-link" className="btn-primary !py-2 !px-5 hidden sm:inline-flex">{t("nav.register")}</Link>
              </>
            )}
            <button className="lg:hidden btn-ghost !p-2" data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} aria-label="Menu">{open ? <X size={20} /> : <Menu size={20} />}</button>
          </div>
        </div>
        {open && <nav className="lg:hidden border-t border-craft bg-linen px-5 py-3 flex flex-col gap-1" data-testid="nav-mobile">{links.map((l) => renderLink(l, "btn-ghost !justify-start"))}</nav>}
      </header>
      <main className={`mx-auto w-full flex-1 px-5 py-10 ${wide ? "max-w-[1400px]" : "max-w-6xl"}`}>{children}</main>
      <footer className="border-t border-craft/80 py-8 text-center text-xs text-ink/50 space-y-2">
        <div>Ci penso io · {t("brand.payoff")} · <Link to="/casa-e-villa" className="underline">{t("nav.casaVilla")}</Link> · <Link to="/cura-degli-interni" className="underline">{t("nav.interni")}</Link> · <Link to="/365" className="underline">{t("nav.p365")}</Link> · <a href="/#contatti" className="underline">{t("nav.contatti")}</a>{user && <> · <Link to={ROLE_HOME[user.role]} className="underline">{t("nav.yourArea")}</Link></>}</div>
        <div data-testid="footer-legal"><span className="font-serif text-sm text-ink/70">Ci penso io</span> by Elit Manutenzioni e Gestioni S.r.l. · Via Dieta di Bari 16/H · 70121 Bari · P.IVA 02194820441</div>
      </footer>
    </div>
  );
}
